/**
 * Generic OData V4 client for the SAP backend option.
 *
 * Part of the isolated `sap-backend/` side project (see README.md). This is the
 * reusable foundation every entity adapter builds on. The error-envelope
 * unpacking mirrors the proven pattern in currencyRatesService.ts /
 * wbsActualsService.ts (CF proxy wraps SAP errors as
 * `{ error: "s4_error", status, detail: <real SAP body> }`).
 *
 * All requests round-trip through the existing `/api/s4/*` proxy chain. No
 * Supabase, no local mirror: this is the live SAP feed by design.
 */

import { SapBackendError } from './errors'

interface ODataCollectionResponse<T> {
  '@odata.context'?: string
  value: T[]
}

interface ODataErrorBody {
  error?: string | { code?: string; message?: string | { value?: string } }
  message?: string
  status?: number
  detail?: ODataErrorBody | string
}

function extractOData(
  body: ODataErrorBody | string | undefined,
  fallback: string,
): { code: string | null; message: string } {
  if (!body) return { code: null, message: fallback }
  if (typeof body === 'string') return { code: null, message: body || fallback }
  // CF-proxy envelope: { error: "s4_error", status, detail: <real SAP body> }
  if (body.detail && (body.error === 's4_error' || typeof body.error === 'string')) {
    return extractOData(body.detail, fallback)
  }
  if (typeof body.error === 'object' && body.error) {
    const m = body.error.message
    const text = typeof m === 'string' ? m : m?.value
    return { code: body.error.code ?? null, message: text || fallback }
  }
  if (typeof body.message === 'string' && body.message) return { code: null, message: body.message }
  return { code: null, message: fallback }
}

async function unpackError(res: Response): Promise<SapBackendError> {
  let body: ODataErrorBody | string | undefined
  try {
    body = (await res.json()) as ODataErrorBody
  } catch {
    try { body = await res.text() } catch { /* ignore */ }
  }
  const fallback = `SAP returned HTTP ${res.status}`
  const { code, message } = extractOData(body, fallback)
  return new SapBackendError(res.status, code, message)
}

/** Escape an OData string literal (double single-quotes). Caller is
 *  responsible for URL-encoding when the value is placed in a key segment. */
export function odataLiteral(v: string): string {
  return v.replace(/'/g, "''")
}

/** GET an OData V4 entity collection and return its `value` array. */
export async function odataGetCollection<T>(url: string): Promise<T[]> {
  const res = await fetch(url, { headers: { Accept: 'application/json' } })
  if (!res.ok) throw await unpackError(res)
  const body = (await res.json()) as ODataCollectionResponse<T>
  return body.value ?? []
}

/** GET one page of an OData V4 entity collection together with the
 *  `@odata.count` the server returns when the request asked for `$count=true`.
 *  `count` is null when the server sent none. */
export async function odataGetPage<T>(url: string): Promise<{ value: T[]; count: number | null }> {
  const res = await fetch(url, { headers: { Accept: 'application/json' } })
  if (!res.ok) throw await unpackError(res)
  const body = (await res.json()) as ODataCollectionResponse<T> & { '@odata.count'?: number | string }
  const raw = body['@odata.count']
  const count = raw === undefined || raw === null || raw === '' ? null : Number(raw)
  return { value: body.value ?? [], count: count !== null && Number.isFinite(count) ? count : null }
}

/** POST a JSON body to an OData V4 endpoint (entity create or bound action). */
export async function odataPost<T>(url: string, payload: unknown): Promise<T> {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(payload),
  })
  if (!res.ok) throw await unpackError(res)
  return (await res.json()) as T
}
